import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {PositionAreaEnum, WeekdayEnum} from "@/modules/base/enums";
import {Negotiator, StaffMember} from "@/modules/staff/entity";
import {Scout} from "@/modules/scouting/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import GateManager from "@/modules/action-points/services/GateManager";
import {GateTypeEnum} from "@/modules/action-points/enums";
import {Coach} from "@/modules/training/entity";
import {Collection} from "collect.js";

@injectable()
export class ActionPointsGenerationEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(GateManager) private gateManager: GateManager,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterDayEvaluationEvent, callback: this.onAfterDayEvaluationEvent},
    ];
  }

  async onAfterDayEvaluationEvent(event: AfterDayEvaluationEvent): Promise<void> {
    if (event.season.currentDate.weekday() !== WeekdayEnum.WEDNESDAY) {
      return;
    }

    const staffMembers = collect(
      await StaffMember
        .createQueryBuilder('member')
        .innerJoinAndSelect('member.team', 'team')
        .where('team.user = true')
        .getMany()
    );

    const actionPointsNegotiating = staffMembers
      .filter(m => m instanceof Negotiator)
      .map(this.configRepository.get('staff.actionPointGeneration.negotiating'))
      .sum() as number;

    const actionPointsScouting = staffMembers
      .filter(m => m instanceof Scout)
      .map(this.configRepository.get('staff.actionPointGeneration.scouting'))
      .sum() as number;

    const actionPointsTrainingGK = this.getActionPointsFor(staffMembers, PositionAreaEnum.GK, .1);
    const actionPointsTrainingDF = this.getActionPointsFor(staffMembers, PositionAreaEnum.DF, .2);
    const actionPointsTrainingMF = this.getActionPointsFor(staffMembers, PositionAreaEnum.MF, .2);
    const actionPointsTrainingAT = this.getActionPointsFor(staffMembers, PositionAreaEnum.AT, .2);

    await this.gateManager.getGate(GateTypeEnum.NEGOTIATION).currency.add(actionPointsNegotiating as number);
    await this.gateManager.getGate(GateTypeEnum.SCOUTING).currency.add(actionPointsScouting as number);
    await this.gateManager.getGate(GateTypeEnum.TRAINING_GK).currency.add(actionPointsTrainingGK as number);
    await this.gateManager.getGate(GateTypeEnum.TRAINING_DF).currency.add(actionPointsTrainingDF as number);
    await this.gateManager.getGate(GateTypeEnum.TRAINING_MF).currency.add(actionPointsTrainingMF as number);
    await this.gateManager.getGate(GateTypeEnum.TRAINING_AT).currency.add(actionPointsTrainingAT as number);

    console.debug(`Generate action points`, {
      actionPointsNegotiating,
      actionPointsScouting,
      actionPointsTrainingGK,
      actionPointsTrainingDF,
      actionPointsTrainingMF,
      actionPointsTrainingAT,
    });
  }

  private getActionPointsFor(staffMembers: Collection<StaffMember>, area: PositionAreaEnum, allPositionFactor: number): number {
    let actionPoints = staffMembers
      .filter(m => m instanceof Coach && m.positionArea === area)
      .map((m) => this.configRepository.get('staff.actionPointGeneration.training')(m as Coach))
      .sum() as number;

    actionPoints += allPositionFactor * (
      staffMembers
        .filter(m => m instanceof Coach && !m.positionArea)
        .map((m) => this.configRepository.get('staff.actionPointGeneration.trainingAll')(m as Coach, area))
        .sum() as number
    );

    return actionPoints;
  }
}